import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { LineChart } from './Charts.tsx';
import { EmptyState, Loading } from './ui.tsx';
import { formatDate, formatNumber } from '../lib/format.ts';
import type { MetricSeries, Platform } from '../../../shared/types.ts';

/**
 * Resumo de crescimento em linguagem simples, não um gráfico técnico: a
 * pergunta que importa para a equipe é "estamos subindo ou descendo", não a
 * forma exata da curva. O gráfico entra como apoio visual, não como o dado
 * principal — por isso o texto vem primeiro e maior.
 */
export function FollowerGrowth({ platform }: { platform: Platform }) {
  const series = useAsync(
    () => api.get<MetricSeries>(`/metrics/series?platform=${platform}&metric=followers`),
    [platform],
  );

  if (series.loading) return <Loading label="Carregando evolução de seguidores…" />;
  if (series.error) return null;

  const points = series.data?.points ?? [];
  if (points.length === 0) {
    return (
      <EmptyState
        title="Ainda não há histórico suficiente."
        hint="A partir da segunda rodada de auditoria, este gráfico mostra se os seguidores estão subindo ou descendo."
      />
    );
  }

  if (points.length === 1) {
    return (
      <div className="growth-summary">
        <p className="text-body">
          Só há uma medição até agora: <strong>{formatNumber(points[0]!.value)}</strong> seguidores em{' '}
          {formatDate(points[0]!.date)}.
        </p>
        <p className="text-micro text-muted">
          Assim que uma segunda rodada de auditoria for feita, este espaço passa a mostrar se o número
          está subindo ou descendo.
        </p>
      </div>
    );
  }

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const delta = last.value - first.value;
  const pct = first.value !== 0 ? (delta / first.value) * 100 : 0;
  const direction: 'up' | 'down' | 'flat' = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';

  const daySpan = Math.max(
    1,
    Math.round(
      (new Date(`${last.date}T00:00:00Z`).getTime() - new Date(`${first.date}T00:00:00Z`).getTime()) /
        86_400_000,
    ),
  );

  const ARROW = { up: '↑', down: '↓', flat: '→' } as const;
  const VERB = { up: 'subiu', down: 'caiu', flat: 'ficou estável' } as const;
  const TONE_CLASS = { up: 'growth-summary--up', down: 'growth-summary--down', flat: '' } as const;

  return (
    <div className={`growth-summary ${TONE_CLASS[direction]}`}>
      <div className="growth-summary__headline">
        <span className="growth-summary__arrow" aria-hidden="true">
          {ARROW[direction]}
        </span>
        <p className="text-body">
          Nos últimos {daySpan} dia{daySpan === 1 ? '' : 's'} ({formatDate(first.date)} a{' '}
          {formatDate(last.date)}), o número de seguidores <strong>{VERB[direction]}</strong>
          {direction !== 'flat' && (
            <>
              {' '}
              em <strong>{formatNumber(Math.abs(delta))}</strong>
              {/* Abaixo de 0,1% o arredondamento vira "+0,0%", que soa
                  contraditório com "subiu" — melhor omitir do que confundir. */}
              {Math.abs(pct) >= 0.1 && (
                <>
                  {' '}
                  ({delta > 0 ? '+' : ''}
                  {pct.toFixed(1).replace('.', ',')}%)
                </>
              )}
            </>
          )}
          .
        </p>
      </div>
      <p className="text-micro text-muted growth-summary__now">
        Hoje: <strong className="tabular-nums">{formatNumber(last.value)}</strong> seguidores.
      </p>
      <LineChart points={points} label="Seguidores ao longo do tempo" height={140} />
    </div>
  );
}
